import { z } from "zod";
import { e164PhoneSchema, isoDateSchema, objectIdSchema, paginationQuerySchema, smsSenderSchema, } from "./common";
export const messageDirectionEnum = z.enum(["SENT", "RECEIVED"]);
export const messageStatusEnum = z.enum([
    "PENDING",
    "QUEUED",
    "SENDING",
    "SENT",
    "DELIVERED",
    "FAILED",
    "CANCELLED",
]);
export const messageEncodingEnum = z.enum(["GSM7", "UCS2"]);
export const messageFailureCodeEnum = z.enum([
    "no_device_online",
    "pickup_timeout",
    "sending_timeout",
    "carrier_rejected",
    "invalid_number",
    "opted_out",
    "unknown",
]);
export const messageSchema = z.object({
    _id: objectIdSchema,
    userId: objectIdSchema,
    deviceId: objectIdSchema.nullable(),
    direction: messageDirectionEnum,
    // Nullable on both sides symmetrically: for SENT the device doesn't always reliably report
    // its own outbound number (sender); for RECEIVED the API doesn't always know the device's own
    // number the message arrived on (recipient) — Android doesn't reliably expose it either.
    recipient: e164PhoneSchema.nullable(),
    // Not E.164: an inbound message's sender may be a national-format number, an alphanumeric
    // sender ID or a short code. recipient stays strict — it is either a number we dispatched to,
    // or this device's own SIM.
    sender: smsSenderSchema.nullable(),
    body: z.string().max(1600),
    status: messageStatusEnum,
    simSlot: z.number().int().min(0).max(1).nullable(),
    segments: z.number().int().min(1),
    encoding: messageEncodingEnum,
    scheduledAt: isoDateSchema.nullable(),
    queuedAt: isoDateSchema.nullable(),
    sentAt: isoDateSchema.nullable(),
    deliveredAt: isoDateSchema.nullable(),
    failureCode: messageFailureCodeEnum.nullable(),
    failureReason: z.string().max(500).nullable(),
    retryCount: z.number().int().min(0).default(0),
    batchId: objectIdSchema.nullable(),
    campaignId: objectIdSchema.nullable(),
    clientMsgId: z.string().max(120).nullable(),
    idempotencyKey: z.string().max(200).nullable(),
    createdAt: isoDateSchema,
});
export const sendSmsSchema = z.object({
    recipients: z.array(e164PhoneSchema).min(1).max(500),
    message: z.string().min(1).max(1600),
    deviceId: objectIdSchema.optional(),
    simSlot: z.number().int().min(0).max(1).optional(),
    scheduledAt: isoDateSchema.optional(),
    templateId: objectIdSchema.optional(),
    variables: z.record(z.string(), z.string()).optional(),
});
export const sendBulkJsonSchema = z.object({
    items: z
        .array(z.object({
        recipient: e164PhoneSchema,
        message: z.string().min(1).max(1600).optional(),
        variables: z.record(z.string(), z.string()).optional(),
    }))
        .min(1)
        .max(10_000),
    deviceId: objectIdSchema.optional(),
    templateId: objectIdSchema.optional(),
    scheduledAt: isoDateSchema.optional(),
});
export const listMessagesQuerySchema = paginationQuerySchema.extend({
    direction: messageDirectionEnum.optional(),
    status: messageStatusEnum.optional(),
    deviceId: objectIdSchema.optional(),
    search: z.string().max(120).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
export const batchProgressSchema = z.object({
    batchId: objectIdSchema,
    total: z.number().int().min(0),
    queued: z.number().int().min(0),
    sent: z.number().int().min(0),
    delivered: z.number().int().min(0),
    failed: z.number().int().min(0),
    cancelled: z.number().int().min(0),
});
/** Device-reported status update, batched from the phone. */
export const deviceStatusUpdateSchema = z.object({
    messageId: objectIdSchema,
    status: z.enum(["SENDING", "SENT", "DELIVERED", "FAILED"]),
    failureCode: messageFailureCodeEnum.optional(),
    failureReason: z.string().max(500).optional(),
});
export const deviceStatusBatchSchema = z.object({
    updates: z.array(deviceStatusUpdateSchema).min(1).max(200),
});
/** Inbound SMS reported by the phone — idempotent on clientMsgId. */
export const deviceReceivedMessageSchema = z.object({
    clientMsgId: z.string().min(1).max(120),
    // This was e164PhoneSchema, which rejected any sender without a leading "+" — including an
    // ordinary national-format number like 03324012015. Because the upload is an all-or-nothing
    // batch, that one row blocked every inbound message behind it. See smsSenderSchema.
    sender: smsSenderSchema,
    body: z.string().max(1600),
    simSlot: z.number().int().min(0).max(1).nullable(),
    receivedAt: isoDateSchema,
});
export const deviceReceivedBatchSchema = z.object({
    messages: z.array(deviceReceivedMessageSchema).min(1).max(200),
});
// GSM 03.38 basic charset (simplified) used to decide GSM-7 vs UCS-2 encoding.
const GSM7_BASIC_CHARSET = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1BÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXTENDED_CHARSET = "^{}\\[~]|€";
export function isGsm7Compatible(text) {
    for (const ch of text) {
        if (!GSM7_BASIC_CHARSET.includes(ch) && !GSM7_EXTENDED_CHARSET.includes(ch)) {
            return false;
        }
    }
    return true;
}
/** Mirrors carrier segmentation rules: 160/153-per-segment GSM-7, 70/67-per-segment UCS-2. */
export function computeSegments(text) {
    const gsm7 = isGsm7Compatible(text);
    const encoding = gsm7 ? "GSM7" : "UCS2";
    let extendedCount = 0;
    if (gsm7) {
        for (const ch of text) {
            if (GSM7_EXTENDED_CHARSET.includes(ch))
                extendedCount += 1;
        }
    }
    const effectiveLength = gsm7 ? text.length + extendedCount : text.length;
    const singleLimit = gsm7 ? 160 : 70;
    const multiLimit = gsm7 ? 153 : 67;
    if (effectiveLength <= singleLimit) {
        return { encoding, segments: 1 };
    }
    return { encoding, segments: Math.ceil(effectiveLength / multiLimit) };
}
//# sourceMappingURL=message.js.map